import { useEffect, useState } from "react";
import { api, projectScope } from "../api/client";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Fade from "@mui/material/Fade";
import Grow from "@mui/material/Grow";
import Snackbar from "@mui/material/Snackbar";
import LinearProgress from "@mui/material/LinearProgress";
import CustomButton from "../components/CustomButton";

type ApiKey = {
  id: string; prefix: string; name: string;
  created_at: string; expires_at: string | null;
  revoked_at: string | null; last_used_at: string | null;
};

export function ApiKeys() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [rotated, setRotated] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);

  async function load() {
    setLoading(true);
    try { setKeys(await api.get<ApiKey[]>(projectScope("/api-keys"))); }
    catch (e) { setErr((e as Error).message); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, []);

  async function rotate() {
    setErr(null); setRotated(null); setRotating(true);
    try {
      const resp = await api.post<{ api_key: string }>(projectScope("/api-keys/rotate"));
      setRotated(resp.api_key);
      await load();
    } catch (e) { setErr((e as Error).message); }
    finally { setRotating(false); }
  }

  const activeCount = keys.filter((k) => !k.revoked_at).length;
  const revokedCount = keys.filter((k) => k.revoked_at).length;

  return (
    <Box className="orgs-page">
      {/* Page header with action */}
      <Fade in timeout={400}>
        <Box className="orgs-header">
          <Box>
            <Typography variant="h5" className="orgs-page-title">
              API Keys
            </Typography>
            <Typography variant="body2" className="login-subtitle" sx={{ mb: 0 }}>
              Manage authentication keys for this project.
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <CustomButton
            loading={rotating}
            buttonText="Rotate API key"
            buttonTextLoading="Rotating…"
            handleSubmitClick={rotate}
          />
        </Box>
      </Fade>

      {/* Error Snackbar */}
      <Snackbar
        open={!!err}
        autoHideDuration={3000}
        onClose={() => setErr(null)}
      >
        <Alert
          onClose={() => setErr(null)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {err}
        </Alert>
      </Snackbar>
      {/* New key display */}
      {rotated && (
        <Fade in timeout={300}>
          <Alert
            severity="success"
            sx={{ mb: 3, borderRadius: "var(--radius-md)", border: "1px solid rgba(52, 199, 89, 0.3)" }}
            onClose={() => setRotated(null)}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              New key generated — copy it now, it won't be shown again.
            </Typography>
            <Box className="apikeys-new-key">
              {rotated}
            </Box>
          </Alert>
        </Fade>
      )}

      {/* Summary stats */}
      {!loading && keys.length > 0 && (
        <Fade in timeout={500}>
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <Box className="apikeys-stat-card">
              <Typography className="apikeys-stat-value">{keys.length}</Typography>
              <Typography className="apikeys-stat-label">Total keys</Typography>
            </Box>
            <Box className="apikeys-stat-card">
              <Typography className="apikeys-stat-value apikeys-stat-active">{activeCount}</Typography>
              <Typography className="apikeys-stat-label">Active</Typography>
            </Box>
            {revokedCount > 0 && (
              <Box className="apikeys-stat-card">
                <Typography className="apikeys-stat-value apikeys-stat-revoked">{revokedCount}</Typography>
                <Typography className="apikeys-stat-label">Revoked</Typography>
              </Box>
            )}
          </Box>
        </Fade>
      )}

      {/* Keys table */}
      <Grow in timeout={600}>
        <Card className="orgs-create-card">
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            {loading ? (
              <Box sx={{ p: 3 }}>
                <LinearProgress sx={{ borderRadius: 2, "& .MuiLinearProgress-bar": { backgroundColor: "var(--brand-pink)" } }} />
                <Typography variant="body2" className="login-subtitle" sx={{ mt: 2, mb: 0 }}>
                  Loading API keys…
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className="projects-th" sx={{ width: "22%" }}>Prefix</TableCell>
                      <TableCell className="projects-th" sx={{ width: "20%" }}>Name</TableCell>
                      <TableCell className="projects-th" sx={{ width: "25%" }}>Created</TableCell>
                      <TableCell className="projects-th" sx={{ width: "18%" }}>Last used</TableCell>
                      <TableCell className="projects-th" sx={{ width: "15%" }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {keys.map((k, index) => (
                      <TableRow key={k.id} className="projects-row stagger-item" style={{ animationDelay: `${index * 0.05}s` }}>
                        <TableCell className="projects-td">
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box className="apikeys-prefix-icon">🔑</Box>
                            <Typography component="span" sx={{ fontFamily: "'SF Mono', 'Fira Code', monospace", fontSize: 13 }}>
                              {k.prefix}…
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell className="projects-td">
                          <Typography sx={{ fontWeight: 500, fontSize: 14 }}>{k.name}</Typography>
                        </TableCell>
                        <TableCell className="projects-td" sx={{ color: "var(--brand-muted) !important", fontSize: "13px !important" }}>
                          {formatDate(k.created_at)}
                        </TableCell>
                        <TableCell className="projects-td" sx={{ color: "var(--brand-muted) !important", fontSize: "13px !important" }}>
                          {k.last_used_at ? formatDate(k.last_used_at) : "—"}
                        </TableCell>
                        <TableCell className="projects-td">
                          {k.revoked_at ? (
                            <Chip label="Revoked" size="small" className="projects-status-chip failed" />
                          ) : k.expires_at ? (
                            <Chip label={`Expires ${k.expires_at}`} size="small" className="projects-status-chip pending" />
                          ) : (
                            <Chip label="Active" size="small" className="projects-status-chip succeeded" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}

                    {keys.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ py: 6 }}>
                          <Box className="empty-state">
                            <Box className="empty-state-icon">🔑</Box>
                            <Typography className="empty-state-title">No API keys found</Typography>
                            <Typography className="empty-state-desc">
                              Rotate to generate your first API key for this project.
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Grow>
    </Box>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = d.getDate();
  const suffix = [11, 12, 13].includes(day) ? "th" : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[day % 10] || "th";
  const month = d.toLocaleString(undefined, { month: "short" });
  const year = d.getFullYear();
  let hours = d.getHours();
  const mins = d.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;
  return `${day}${suffix} ${month} ${year}, ${hours}:${mins}${ampm}`;
}
